import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import {
    fromOthersNodeName,
    workflowRepository,
} from "motw_core/Repositories/WorkflowRepository";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";

interface IBookOfMindNodeIndex {
    nodes: IWorkflowNode[];
    nodesById: Map<number, IWorkflowNode>;
}

let cachedIndex: IBookOfMindNodeIndex | undefined;
let indexPromise: Promise<IBookOfMindNodeIndex> | undefined;
let cacheGeneration = 0;

export function GetBookOfMindNodeIndexAsync() {
    if (cachedIndex) {
        return Promise.resolve(cachedIndex);
    }

    if (!indexPromise) {
        const requestGeneration = cacheGeneration;
        const request = BuildBookOfMindNodeIndexAsync()
            .then((index) => {
                if (requestGeneration === cacheGeneration) {
                    cachedIndex = index;
                }
                return index;
            })
            .finally(() => {
                if (indexPromise === request) {
                    indexPromise = undefined;
                }
            });
        indexPromise = request;
    }

    return indexPromise;
}

export function ClearBookOfMindNodeIndex() {
    cacheGeneration += 1;
    cachedIndex = undefined;
    indexPromise = undefined;
}

export async function SearchBookOfMindNodesAsync(
    query: string,
    readingListNodeNames: string[]
) {
    const index = await GetBookOfMindNodeIndexAsync();
    const selectedNames = new Set(
        readingListNodeNames.map((name) => name.toLowerCase())
    );
    const idText = query.startsWith("#") ? query.substring(1) : query;
    const normalizedQuery = query.toLowerCase();
    const matches = /^\d+$/.test(idText)
        ? index.nodes.filter((node) => node.id === Number(idText))
        : index.nodes.filter((node) =>
              node.name.toLowerCase().includes(normalizedQuery)
          );

    const matchesWithContainingNodes = matches.map((node) => ({
        node,
        containingNode: FindSelectedAncestor(
            node,
            selectedNames,
            index
        ),
    }));
    return {
        results: matchesWithContainingNodes.map(
            ({ node, containingNode }) => ({
                node,
                containingNodeName: containingNode?.name,
            })
        ),
    };
}

export async function GetNonOverlappingNodeNamesAsync(
    selectedNode: IWorkflowNode,
    currentNodeNames: string[]
) {
    const index = await GetBookOfMindNodeIndexAsync();
    const currentNames = new Set(
        currentNodeNames.map((name) => name.toLowerCase())
    );
    const containingNode = FindSelectedAncestor(
        selectedNode,
        currentNames,
        index
    );

    if (containingNode) {
        return {
            nodeNames: currentNodeNames,
            containingNodeName: containingNode.name,
        };
    }

    return {
        nodeNames: [
            ...currentNodeNames.filter(
                (name) =>
                    !IsDescendantNameOfNode(name, selectedNode, index)
            ),
            selectedNode.name,
        ],
        containingNodeName: undefined,
    };
}

async function BuildBookOfMindNodeIndexAsync() {
    const allNodes = await workflowRepository.GetAllNodesAsync(
        ClientDataProvider
    );
    const rootNode = allNodes.find(
        (node) => node.name === fromOthersNodeName
    );

    if (!rootNode) {
        return { nodes: [], nodesById: new Map<number, IWorkflowNode>() };
    }

    const childrenByParentId = new Map<number, IWorkflowNode[]>();
    allNodes.forEach((node) => {
        const siblings = childrenByParentId.get(node.parentid) ?? [];
        siblings.push(node);
        childrenByParentId.set(node.parentid, siblings);
    });

    const nodes: IWorkflowNode[] = [];
    const pending = [rootNode];

    while (pending.length > 0) {
        const node = pending.pop()!;
        nodes.push(node);
        pending.push(...(childrenByParentId.get(node.id) ?? []));
    }

    return {
        nodes,
        nodesById: new Map(nodes.map((node) => [node.id, node])),
    };
}

function FindSelectedAncestor(
    node: IWorkflowNode,
    selectedNames: Set<string>,
    index: IBookOfMindNodeIndex
) {
    let currentNode: IWorkflowNode | undefined = node;

    while (currentNode) {
        if (selectedNames.has(currentNode.name.toLowerCase())) {
            return currentNode;
        }
        currentNode = index.nodesById.get(currentNode.parentid);
    }

    return undefined;
}

function IsDescendantNameOfNode(
    possibleDescendantName: string,
    ancestorNode: IWorkflowNode,
    index: IBookOfMindNodeIndex
) {
    return index.nodes
        .filter((node) => node.name === possibleDescendantName)
        .some((node) => {
            let currentNode = index.nodesById.get(node.parentid);

            while (currentNode) {
                if (currentNode.id === ancestorNode.id) {
                    return true;
                }
                currentNode = index.nodesById.get(currentNode.parentid);
            }

            return false;
        });
}
